<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div
      v-observe-visibility="visibilityChanged"
      class="live-map-container">
      <div
        id="livemapInternal"
        class="live-map" />
      <div class="live-queue">
        <div class="header">
          <icon-colored-playback />
          {{ capitalize($t('messages.latestAdPlays')) }}
        </div>
        <ul>
          <!-- We don't want the `key` attribute used here, it's causing some issues when elements are recycled. -->
          <!-- eslint-disable-next-line -->
          <li
            v-for="lp in queueReversed"
            v-show="lp.show != false">
            <img
              :src="lp.creativeFileThumbnailUrl"
              @error="lp.show = false" />
          </li>
        </ul>
      </div>
    </div>
    <div
      v-if="showBarChart"
      class="creative-file-stats-container">
      <div
        v-for="cfs in sortedPlayedCreativeFiles.slice(0, 10)"
        :key="cfs.id"
        class="creative-file-stats">
        <div class="bar-container">
          <div
            class="bar"
            :style="`height: ${mostPlayedCreativeFileCount === 0 ? 0 : (100 * playedCreativeFilesStats[cfs.creativeFileUrl]) / mostPlayedCreativeFileCount}%;`" />
        </div>
        <span class="badge badge-rounded badge-info-hollow">{{
          formatNumber(playedCreativeFilesStats[cfs.creativeFileUrl], '0,0')
        }}</span>
        <img :src="cfs.creativeFileThumbnailUrl" />
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import { DateTime } from 'luxon';
  import mapboxgl from 'mapbox-gl';
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import { IconColoredPlayback } from '@/common/icons/colored';
  import MapStyleControl from '@/org-app/components/geofenceGroups/MapStyleControl';
  import formatNumber from '@/utils/NumberFormatter';

  export default {
    components: {
      IconColoredPlayback,
    },
    props: {
      countPerLoad: {
        type: Number,
        default: 1000,
      },
      lineItemID: {
        type: Number,
        default: null,
      },
      showBarChart: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        // displayActualTime: DateTime.local(),
        // displayVirtualTime: DateTime.local(),
        badCreativeUrls: [],
        boundsEast: -180,
        boundsNorth: -90,
        boundsSouth: 90,
        boundsWest: 180,
        creativeFilesLoaded: [],
        creativeFilesToLoad: [],
        didPreviouslyRun: false,
        endTime: null,
        isMapLoaded: false,
        latestPlays: [],
        map: null,
        offsetBetweenRealTimeAndVirtualTime: { seconds: 60 },
        maxLookbackWindowTimePeriod: { seconds: 60 },
        playedCreativeFilesStats: {},
        playedCreativeFiles: [],
        queue: [],
        startTime: null,
        mostPlayedCreativeFileCount: 0,
      };
    },
    computed: {
      queueReversed() {
        return [...this.queue].reverse();
      },
      sortedPlayedCreativeFiles() {
        return [...this.playedCreativeFiles].sort((a, b) =>
          this.playedCreativeFilesStats[a.creativeFileUrl] > this.playedCreativeFilesStats[b.creativeFileUrl] ? -1 : 1,
        );
      },
    },
    methods: {
      capitalize,
      ...mapActions('orgApp', ['getLatestPlays']),
      formatNumber,
      showPlayOnMap(lp) {
        if (this.badCreativeUrls.indexOf(lp.creativeFileID) > -1) {
          return;
        }

        if (this.playedCreativeFilesStats[lp.creativeFileUrl] == null) {
          this.playedCreativeFilesStats[lp.creativeFileUrl] = 0;
          this.playedCreativeFiles.push({
            creativeFileID: lp.creativeFileID,
            creativeFileUrl: lp.creativeFileUrl,
            creativeFileThumbnailUrl: lp.creativeFileThumbnailUrl,
          });
        }
        if (this.showBarChart) {
          if (this.mostPlayedCreativeFileCount === this.playedCreativeFilesStats[lp.creativeFileUrl]) {
            this.mostPlayedCreativeFileCount += 1;
          }
        }
        this.playedCreativeFilesStats[lp.creativeFileUrl] += 1;

        const maxQueueSize = 20;

        this.queue =
          this.queue.length >= maxQueueSize
            ? [...this.queue.slice(this.queue.length - maxQueueSize, this.queue.length), lp]
            : [...this.queue, lp];

        const mapBounds = this.map.getBounds();
        if (
          lp.longitude < mapBounds.getWest() ||
          lp.longitude > mapBounds.getEast() ||
          lp.latitude < mapBounds.getSouth() ||
          lp.latitude > mapBounds.getNorth()
        ) {
          return;
        }

        // Apply a class that modulates the duration ads are displayed for (only way to change pseudo classes styles).
        // Helps with CPU usage.
        const stayUpForXSeconds = Math.max(1, Math.ceil((100 - this.visibleMarketCount) / 10));
        const durationClass = `d${stayUpForXSeconds}`;

        // create a DOM element for the marker
        const el = document.createElement('div');
        el.className = `marker arrow_box ${durationClass}`;
        const img = document.createElement('img');
        img.src = lp.creativeFileThumbnailUrl;
        img.onerror = () => {
          el.style.display = 'none';
          this.badCreativeUrls.push(lp.creativeFileID);
        };
        img.style.height = '32px';
        el.appendChild(img);

        // add marker to map
        const marker = new mapboxgl.Marker({ element: el, offset: [0, -30] })
          .setLngLat([lp.longitude, lp.latitude])
          .addTo(this.map);
        this.visibleMarketCount += 1;

        setTimeout(() => {
          marker.remove();
          this.visibleMarketCount -= 1;
        }, stayUpForXSeconds * 1000);
      },
      refreshPlays() {
        this.didPreviouslyRun = true;
        const params = {
          count: this.countPerLoad,
          max_lookback_seconds: this.maxLookbackWindowTimePeriod.seconds,
        };
        if (this.lineItemID != null) {
          params.lineitem_id = this.lineItemID;
        }
        this.getLatestPlays({ params }).then((results) => {
          const firstTimeGettingPlays = this.latestPlays.length === 0;
          const groupedPlays = results.reduce((mem, x) => {
            if (mem.find((y) => y.playedOnUtc.equals(x.playedOnUtc) && y.creativeFileID === x.creativeFileID) == null) {
              return [...mem, x];
            }
            return mem;
          }, []);
          groupedPlays.forEach((x) => x.addFuzinessToTime());

          this.latestPlays = groupedPlays;

          if (this.latestPlays.length > 0) {
            this.startTime = this.latestPlays.reduce((earliestDate, play) => {
              if (earliestDate == null) {
                return play.playedOnUtc;
              }
              return earliestDate < play.playedOnUtc ? earliestDate : play.playedOnUtc;
            }, null);
            this.endTime = this.latestPlays.reduce((latestDate, play) => {
              if (latestDate == null) {
                return play.playedOnUtc;
              }
              return latestDate > play.playedOnUtc ? latestDate : play.playedOnUtc;
            }, null);

            this.offsetBetweenRealTimeAndVirtualTime.seconds = DateTime.utc()
              .diff(this.startTime, 'seconds')
              .toObject().seconds;

            const east = Math.max(...this.latestPlays.map((x) => x.longitude));
            const west = Math.min(...this.latestPlays.map((x) => x.longitude));
            const north = Math.max(...this.latestPlays.map((x) => x.latitude));
            const south = Math.min(...this.latestPlays.map((x) => x.latitude));

            if (this.east !== east || this.west !== west || this.north !== north || this.south !== south) {
              this.east = east;
              this.west = west;
              this.north = north;
              this.south = south;
            }

            if (firstTimeGettingPlays) {
              this.map.fitBounds(
                [
                  [west, south],
                  [east, north],
                ],
                {
                  padding: 64,
                  duration: 100,
                  animate: true,
                },
              );
              this.startAnimation();
            }
            // Refresh records.
            this.refreshTimer = setTimeout(
              this.refreshPlays,
              this.endTime.diff(this.startTime, 'seconds').toObject().seconds * 1000,
            );
          } else {
            //
            // No records found, increase look back window if possible and refresh.
            //
            let delay = 0;
            // If the look back window is already more than 60 minutes, don't increase it.
            if (this.maxLookbackWindowTimePeriod.seconds < 3600) {
              this.maxLookbackWindowTimePeriod.seconds *= 2;
            } else {
              delay = 600000; // Wait 1 minute before refreshing the data if the look back window is already more than 1 hour.
            }
            this.refreshTimer = setTimeout(this.refreshPlays, delay);
          }
        });
      },
      startAnimation() {
        this.isAnimationRunning = true;
        const intervalInMs = 100;
        this.animationInterval = setInterval(() => {
          const virtualTime = DateTime.local().minus(this.offsetBetweenRealTimeAndVirtualTime);
          const until = virtualTime.plus(intervalInMs);
          const activePlays = this.latestPlays.filter((lp) => lp.playedOnUtc >= virtualTime && lp.playedOnUtc < until);

          activePlays.forEach((p) => this.showPlayOnMap(p));
        }, intervalInMs);
      },
      stopAnimation() {
        this.isAnimationRunning = false;
        clearTimeout(this.refreshTimer);
        clearInterval(this.animationInterval);
      },
      restart() {
        this.latestPlays = [];
        this.refreshPlays();
      },
      initMap(mapOptions) {
        const map = new mapboxgl.Map(mapOptions);
        map.on('load', () => {
          if (this.enableMapStyleControl) {
            this.mapStyleControl = new MapStyleControl();
            this.mapStyleControl.clickHandler = (isSatellite) => {
              if (isSatellite) {
                this.changeMapStyleToSatellite();
              } else {
                this.changeMapStyleToLight();
              }
            };
            this.map.addControl(this.mapStyleControl);
          }

          this.isMapLoaded = true;

          this.refreshPlays();
        });

        map.addControl(new mapboxgl.NavigationControl());
        map.addControl(new mapboxgl.FullscreenControl());

        return map;
      },
      visibilityChanged(isVisible) {
        if (this.isAnimationRunning && !isVisible) {
          this.stopAnimation();
        } else if (isVisible && !this.isAnimationRunning && this.didPreviouslyRun) {
          this.restart();
        }
      },
    },
    mounted() {
      this.visibleMarketCount = 0;
      setTimeout(() => {
        const mapOptions = {
          container: 'livemapInternal',
          style: 'mapbox://styles/mapbox/light-v9',
          minzoom: 1.3,
          zoom: 1,
        };
        this.map = this.initMap(mapOptions);
      }, 500);
    },
    beforeUnmount() {
      this.stopAnimation();
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  .live-map-container {
    position: relative;
    display: flex;
    border: solid 1px #ccc;
    max-height: calc(100vh - 50rem);
  }

  .live-map {
    flex-basis: 100%;
    margin: auto;
    height: calc(100vh - 50.2rem);
    background: #fff;
  }

  .arrow_box {
    background: #fff;
    border: 0.1rem solid #ccc;
    padding: 0.3rem;

    img {
      max-height: 32px;
      max-width: 128px;
    }

    box-shadow: 0 0.1rem 0.3rem #00000020;

    animation:
      arrow-box-fade-in 0.1s 1,
      arrow-box-fade-out 0.5s 1;
    -webkit-animation:
      arrow-box-fade-in 0.1s 1,
      arrow-box-fade-out 0.5s 1;
    animation-fill-mode: forwards;

    animation-delay: 0s, 5s;
    -webkit-animation-delay: 0s, 5s; /* Safari and Chrome */
    -webkit-animation-fill-mode: forwards;
  }

  .arrow_box:after,
  .arrow_box:before {
    top: 100%;
    left: 50%;
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  .arrow_box:after {
    border-color: rgba(136, 183, 213, 0);
    border-top-color: #fff;
    border-width: 10px;
    margin-left: -10px;

    animation:
      arrow-box-after-fade-in 0.1s 1,
      arrow-box-fade-out 0.5s 1;
    -webkit-animation:
      arrow-box-after-fade-in 0.1s 1,
      arrow-box-fade-out 0.5s 1;
    animation-fill-mode: forwards;

    animation-delay: 0s, 5s;
    -webkit-animation-delay: 0s, 5s; /* Safari and Chrome */
    -webkit-animation-fill-mode: forwards;
  }

  .arrow_box:before {
    border-color: rgba(194, 225, 245, 0);
    border-top-color: #ccc;
    border-width: 11px;
    margin-left: -11px;

    animation:
      arrow-box-before-fade-in 0.1s 1,
      arrow-box-fade-out 0.5s 1;
    -webkit-animation:
      arrow-box-before-fade-in 0.1s 1,
      arrow-box-fade-out 0.5s 1;
    animation-fill-mode: forwards;

    animation-delay: 0s, 5s;
    -webkit-animation-delay: 0s, 5s; /* Safari and Chrome */
    -webkit-animation-fill-mode: forwards;
  }

  .arrow_box.d1,
  .arrow_box.d1:after,
  .arrow_box.d1:before {
    animation-delay: 0s, 1s;
    -webkit-animation-delay: 0s, 1s; /* Safari and Chrome */
  }

  .arrow_box.d2,
  .arrow_box.d2:after,
  .arrow_box.d2:before {
    animation-delay: 0s, 2s;
    -webkit-animation-delay: 0s, 2s; /* Safari and Chrome */
  }

  .arrow_box.d3,
  .arrow_box.d3:after,
  .arrow_box.d3:before {
    animation-delay: 0s, 3s;
    -webkit-animation-delay: 0s, 3s; /* Safari and Chrome */
  }

  .arrow_box.d4,
  .arrow_box.d4:after,
  .arrow_box.d4:before {
    animation-delay: 0s, 4s;
    -webkit-animation-delay: 0s, 4s; /* Safari and Chrome */
  }

  .arrow_box.d5,
  .arrow_box.d5:after,
  .arrow_box.d5:before {
    animation-delay: 0s, 5s;
    -webkit-animation-delay: 0s, 5s; /* Safari and Chrome */
  }

  .arrow_box.d6,
  .arrow_box.d6:after,
  .arrow_box.d6:before {
    animation-delay: 0s, 6s;
    -webkit-animation-delay: 0s, 6s; /* Safari and Chrome */
  }

  .arrow_box.d7,
  .arrow_box.d7:after,
  .arrow_box.d7:before {
    animation-delay: 0s, 7s;
    -webkit-animation-delay: 0s, 7s; /* Safari and Chrome */
  }

  .arrow_box.d8,
  .arrow_box.d8:after,
  .arrow_box.d8:before {
    animation-delay: 0s, 8s;
    -webkit-animation-delay: 0s, 8s; /* Safari and Chrome */
  }

  .arrow_box.d9,
  .arrow_box.d9:after,
  .arrow_box.d9:before {
    animation-delay: 0s, 9s;
    -webkit-animation-delay: 0s, 9s; /* Safari and Chrome */
  }

  .arrow_box.d10,
  .arrow_box.d10:after,
  .arrow_box.d10:before {
    animation-delay: 0s, 10s;
    -webkit-animation-delay: 0s, 10s; /* Safari and Chrome */
  }

  @keyframes arrow-box-fade-in {
    from {
      height: 0;
    }
    to {
      height: 40px;
    }
  }

  @-webkit-keyframes arrow-box-fade-in {
    from {
      height: 0;
    }
    to {
      height: 40px;
    }
  }

  @keyframes arrow-box-fade-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @-webkit-keyframes arrow-box-fade-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @keyframes arrow-box-before-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @-webkit-keyframes arrow-box-before-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes arrow-box-after-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @-webkit-keyframes arrow-box-after-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .live-queue {
    flex-basis: 19rem;
    display: flex;
    flex-direction: column;
    border-left: solid 1px #ccc;

    .header {
      display: flex;
      align-items: center;
      font-weight: 600;
      padding: 0.8rem 1.2rem;
      background: #fff;
      font-size: 0.8em;
      text-align: center;

      svg {
        height: 1.6rem;
        margin-right: 0.6rem;
      }
    }

    ul {
      flex-grow: 1;
      padding: 1rem;
      background: #ccc;
      box-shadow: inset 0 0 6px #00000077;
      margin: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      overflow: hidden;

      li {
        list-style: none;
        margin-bottom: 0.5rem;

        img {
          height: 32px;
          box-shadow: 0 0.1rem 0.3rem #00000020;
          animation: bounce-in 0.2s;
        }
      }
    }
  }

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @-webkit-keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes fade-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  @-webkit-keyframes fade-out {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }

  .creative-file-stats-container {
    display: flex;

    .creative-file-stats {
      display: flex;
      align-items: center;
      flex-direction: column;
      margin-right: 0.6rem;

      .bar-container {
        width: 100%;
        height: 4.8rem;
        position: relative;
        margin-bottom: 0.3rem;

        .bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;

          background: var(--primaryColor);
        }
      }

      img {
        width: 4.8rem;
      }

      .badge {
        margin-bottom: 0.6rem;
      }
    }
  }
</style>
